/* eslint-disable no-undef */
import { getSessionId, getStageCustomerFeedback, mapCurrentUser } from "@redux/memoizedSelectors";
import { EMPTY_GUID, importAll } from "@utils/helpers";
import { ConnectorSource } from "@utils/optionsSets/OptionSets";
import AddComment_popup from "features/comments/components/comment-popup/add-comment_popup";
import { deleteCustomerFeedback, upsertCustomerFeedback } from "features/index";
import EditFeedbackData from "features/journey-map/modals/AddFeedback/EditFeedbackData";
import { addIconCustomSwimlane, deleteIconCustomSwimlane } from "features/journey-map/reducer/IconService";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { IconDisplay } from "shared/components/IconDisplay";
import IconSelector from "shared/popovers/IconSelector";
import { ActionsDropdown, FlexColumnGap, FlexColumnJustifyBetween, IconDispatcher, Label } from "shared/ui-basics/index";
import { SmileyStaticComponent } from "shared/ui-basics/ui-components/EmotionLevelComponents/SmileyStaticComponent";
import { useModal } from "use-modal-hook";
import * as Style from "./Styles";
import { makeGetTouchpoint } from "redux/memoizedSelectors";
import { PermissionManager } from "utils/permissionManager";

export const getImgConnector = (connectorSource) => {
	const integrations = importAll(require.context("@Assets/images/integration", false, /\.(png|jpe?g|svg)$/));

	switch (connectorSource) {
		// case ConnectorSource.Manual:
		// 	return "PEN-TO-SQUARE";
		// case ConnectorSource.ImportFile:
		// 	return "UPLOAD-LIGHT";
		case ConnectorSource.Qualtrics:
			return integrations["qualtrics.png"]?.default;
		case ConnectorSource.SurveyMonkey:
			return integrations["survey-monkey.png"]?.default;
		case ConnectorSource.Sandsiv:
			return integrations["sandsiv.png"]?.default;
	}
	return "";
};
export const StagesCustomerFeedbacks = ({ stage, swimlaneKey }) => {
	const { personaIdStr } = useLocation().search.getAllMapsParams();
	const customerFeedbacks = useSelector(getStageCustomerFeedback(stage.id, personaIdStr)) || [];
	return (
		<>
			{customerFeedbacks?.length > 0 && (
				<FlexColumnGap className="pb-2" gap={4}>
					{customerFeedbacks?.map((customerFeedback) => (
						<CustomerFeedbackCard key={customerFeedback?.id} customerFeedback={customerFeedback} stageId={stage.id} swimlaneKey={swimlaneKey} />
					))}
				</FlexColumnGap>
			)}
		</>
	);
};

export const CustomerFeedbackCard = ({ customerFeedback, stageId, swimlaneKey }) => {
	const { t } = useTranslation();
	const { cjmIdStr: cjmId, personaIdStr: personaId } = useLocation().search.getAllMapsParams();
	const current_user = useSelector(mapCurrentUser);
	const { customerFeedbackDatas } = customerFeedback || [];
	const connectorData = useSelector((state) => state.settings.connector)?.find((con) => con?.id === customerFeedback?.connectorId);
	const touchPoint = useSelector(makeGetTouchpoint(customerFeedback?.touchPointId))

	const metricsData = useSelector((state) => state.libraries.cxMetrics)?.getById(customerFeedback?.cxMetricsId);
	const isOwner = current_user?.roleName?.toLowerCase().toLowerCase() === "owner";
	const isViewer = current_user?.roleName?.toLowerCase().toLowerCase() === "viewer";
	const { discussionIds } = useSelector((state) => state.maps.current_map?.cjmCustomerFeedback?.[personaId]?.[stageId]?.find((cf) => cf.id === customerFeedback.id) || {});
	const [feedbackModal] = useModal(EditFeedbackData, { customerFeedbackId: customerFeedback?.id, stageId });
	const open = useSelector((state) => discussionIds?.includes(state.ui?.comment?.openDiscussion) || false);
	const userAccess = useSelector((state) => state?.maps?.currentUser?.roleName);
	const { swimlaneItemActionsAllowed, addCommentAllowed } = PermissionManager({ userAccess });
	const srcImg = getImgConnector(connectorData?.source);

	const iconParams = {
		swimlaneKey: swimlaneKey,
		stageId: stageId,
		cjmIdStr: cjmId,
		personaIdStr: personaId,
		cardId: customerFeedback?.id,
		type: 28,
		specificKey: swimlaneKey + "_" + stageId + "_" + customerFeedback?.id,
		attributeId: customerFeedback?.id,
	};
	const addIcon = (id) => id && addIconCustomSwimlane({ iconId: id, ...iconParams });
	const deleteIcon = (id) => id && deleteIconCustomSwimlane({ iconId: id, ...iconParams });
	const deleteAction = () => deleteCustomerFeedback({ customerFeedbackId: customerFeedback.id, personaId, stageId, cjmId });
	const copyAction = () => upsertCustomerFeedback({ customerFeedback: { ...customerFeedback, id: EMPTY_GUID, retrieveData: false, icon: [], customerFeedbackDatas: [] }, cjmId });

	const createActions = () => {
		const actions = [];
		if (swimlaneItemActionsAllowed) {
			actions.push({
				handleClick: () => {
					feedbackModal({ customerFeedbackId: customerFeedback?.id, stageId });
				},
				icon: "EDIT",
				text: t("EDIT"),
			});
			actions.push({ handleClick: copyAction, icon: "COPY", text: t("COPY") });

			actions.push({ handleClick: deleteAction, icon: "DELETE", text: t("ASPX_13") });
			actions.push({
				customItem: <IconSelector button={true} selected={customerFeedback?.icons || []} addFunction={addIcon} deleteFunction={deleteIcon} buttonText={t("ICONS")} />,
			});
		}
		if (addCommentAllowed)
			actions.addDiscussionAction({
				objId: customerFeedback.id, swimlaneId: swimlaneKey, stageId: stageId, objName: 'cem_customerfeedback', cjmId, personaId,
			});
		return actions;
	};
	const HeaderCard = () => {
		const getName = customerFeedback?.name?.defaultPlaceholder("FEEDBACK_DATA") || "";

		const actions = createActions()
		return (
			<Style.CustomerFeedbackHeaderWrapper>
				<div className="d-flex align-items-center text-truncate">
					<Style.Logo className="me-2">{IconDispatcher("chart-simple-horizontal")}</Style.Logo>
					<Label color={"#262626"} size={14} fw={600}>{getName}</Label>
				</div>
				<Style.Actions>
					<ActionsDropdown
						size={"xs"}
						isicon={"true"}
						id={customerFeedback.id}
						dropdownClasses="drop-actions"
						actions={actions}
						toggleButton={IconDispatcher("ACTION-DROPDOWN-HORIZONTAL")}
						autoClose={"outside"}
						buttonVariant="light-outline"
					/>

				</Style.Actions>
			</Style.CustomerFeedbackHeaderWrapper>
		);
	};
	const Icons = ({ object, stageId, swimlaneKey, deleteIcon, showEdit }) => {
		return (
			<div className="icon-box-position">
				{object.icons?.map((icon, index) => (
					<IconDisplay key={index} icon={icon} index={index} deleteIcon={deleteIcon} swimlaneKey={swimlaneKey} stageId={stageId} />
				))}
			</div>
		);
	};

	const NonData = () => {
		return (
			<>
				{customerFeedback?.retrieveData ? (
					<Style.DivWrapper className="">
						<Style.LabelNonData size={14}>{t("RETRIEVING_DATA") + "..."}</Style.LabelNonData>
					</Style.DivWrapper>
				) : (
					<Style.DivWrapper className="">
						<Style.LabelNonData size={14}>{t("NEW_FEEDBACK_DATA")}</Style.LabelNonData>
					</Style.DivWrapper>
				)}
			</>
		);
	};
	const CompareValues = () => {
		if (customerFeedbackDatas?.length < 2 || customerFeedbackDatas[0]?.sentimentIndex === customerFeedbackDatas[1]?.sentimentIndex)
			return { icon: "circle", params: { style: "solid", color: "#F0F0F0" } };
		if (customerFeedbackDatas[0]?.sentimentIndex >= customerFeedbackDatas[1]?.sentimentIndex)
			return { icon: "caret-up", params: { size: "lg", style: "solid", color: "#4ADE80" } };
		else
			return { icon: "caret-down", params: { size: "lg", style: "solid", color: "#F87171" } };
	}
	const value = CompareValues();
	const lastSentimentIndex = customerFeedbackDatas[0]?.sentimentIndex;
	return (
		<Style.CustomerFeedbackCardWrapper open={open}>
			<div className="w-100 c-pointer" onClick={isViewer ? () => { } : feedbackModal}>
				<HeaderCard />
				<Style.CustomerFeedbackBodyWrapper key={customerFeedback?.id}>
					<div className="w-100 " >
						{customerFeedbackDatas.length > 0 ? (
							<FlexColumnJustifyBetween className="h-100">
								<Row className="">
									<Col className="d-flex align-items-center gap-2">
										<div key={value.icon}>
											<IconDispatcher type={value.icon} styling="" params={value?.params} />
										</div>
										<Label size={38} fw={600} lh={38} color={"#262626"}>{lastSentimentIndex}</Label>
										{lastSentimentIndex !== 0 && <SmileyStaticComponent emotionLevel={lastSentimentIndex} size={32} />}
									</Col>
								</Row>
								<Row className="pt-3">
									<Col xs={10} className="d-flex align-items-center text-truncate">
										<Style.MomentWrapper className="ps-0 " format="MMM DD, YYYY">
											{customerFeedbackDatas[0]?.interactionDate}
										</Style.MomentWrapper>
										{metricsData ? (
											<span className="d-flex" style={{ lineHeight: '12px' }}>
												<span className="px-2">• </span> <Label color={"#262626"} size={"12"}> {metricsData?.name}</Label>{" "}
											</span>
										) : null}
									</Col>
									<Col xs={2}>
										{srcImg !== "" ? <Style.ImgWrapper className="me-1" src={getImgConnector(connectorData?.source)} /> :
											<>{IconDispatcher(getImgConnector(customerFeedback?.source), "me-1")}</>}

									</Col>
								</Row>
							</FlexColumnJustifyBetween>
						) : (
							<NonData />
						)}
					</div>
					<Icons object={customerFeedback} stageId={stageId} swimlaneKey={swimlaneKey} deleteIcon={deleteIcon} />
				</Style.CustomerFeedbackBodyWrapper>
			</div>
			<div key={discussionIds?.length}>
				<AddComment_popup discussions={discussionIds} subject={`${t('CUSTOMER_FEEDBACK')} - ${touchPoint?.name}`} className="mt-2" />
			</div>
		</Style.CustomerFeedbackCardWrapper>
	);
};
